/**
 * Browser-local product history, kept per signed-in user. No network
 * requests. Each user's history is stored under their own user entity ref, so
 * someone else signing in on the same browser profile never sees it, and it is
 * deleted the first time a different user's history is read or written here:
 * the products a person viewed come from catalog data their permissions
 * allowed, which the next person's may not.
 */
export interface RecentProduct {
  id: string;
  name: string;
  portfolio: string;
  viewedAt: number;
  tagline?: string;
  offeringCount?: number;
}
const PREFIX = 'univerus-hub.products.recent.v2:';
/** The unscoped key used before history was kept per user. */
const LEGACY_KEY = 'univerus-hub.products.recent.v1';
export const RECENT_PRODUCTS_CHANGED = 'univerus-products-history';

const keyFor = (userRef: string) => `${PREFIX}${userRef}`;

/** Deletes the unscoped history and every other user's. */
function dropOtherHistories(userRef: string) {
  const mine = keyFor(userRef);
  const stale: string[] = [LEGACY_KEY];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (key?.startsWith(PREFIX) && key !== mine) stale.push(key);
  }
  for (const key of stale) window.localStorage.removeItem(key);
}

export function readRecentProducts(userRef: string): RecentProduct[] {
  try {
    dropOtherHistories(userRef);
    const value: unknown = JSON.parse(
      window.localStorage.getItem(keyFor(userRef)) || '[]',
    );
    if (!Array.isArray(value)) return [];
    const seen = new Set<string>();
    return value
      .filter((item): item is RecentProduct => {
        if (
          !item ||
          typeof item.id !== 'string' ||
          !/^(?!\.{1,2}$)[a-z0-9._-]+$/i.test(item.id) ||
          typeof item.name !== 'string' ||
          typeof item.portfolio !== 'string' ||
          (item.tagline !== undefined && typeof item.tagline !== 'string') ||
          (item.offeringCount !== undefined &&
            (!Number.isInteger(item.offeringCount) ||
              item.offeringCount < 0)) ||
          typeof item.viewedAt !== 'number' ||
          !Number.isFinite(item.viewedAt) ||
          seen.has(item.id)
        )
          return false;
        seen.add(item.id);
        return true;
      })
      .sort((a, b) => b.viewedAt - a.viewedAt)
      .slice(0, 12);
  } catch {
    return [];
  }
}
export function rememberProduct(
  userRef: string,
  product: Omit<RecentProduct, 'viewedAt'>,
): void {
  try {
    const items = [
      { ...product, viewedAt: Date.now() },
      ...readRecentProducts(userRef).filter(p => p.id !== product.id),
    ].slice(0, 12);
    window.localStorage.setItem(keyFor(userRef), JSON.stringify(items));
    window.dispatchEvent(new Event(RECENT_PRODUCTS_CHANGED));
  } catch {
    /* Storage denial must never prevent opening a product. */
  }
}
